/**
 * 帧信封。见 docs/ARCHITECTURE.md §5.2。
 *
 * ★★ `seq` / `sv` / `epoch` 是三个**正交**的东西，故意不合并 ★★
 *
 *   - `seq`   传输序号。**每 session** 严格 +1，负责顺序与缺口检测。
 *             客户端的丢帧判断**只看它**。
 *   - `sv`    房间状态版本。**每 room**，对某个客户端**可以跳号**（有些 step
 *             只产生私有消息，不 bump sv）。客户端只用它给 `roomSnapshot` 择新。
 *   - `epoch` 服务进程身份（进程启动时生成的 ULID）。变化即要求客户端
 *             `appliedSeq / appliedSv` 全部归零、整表重置。
 *
 * 把 `seq` 与 `sv` 合并成一个数字是最常见的设计错误；
 * **用 `sv` 做丢帧判断会吞掉全部私有消息**——`actionRejected` / `error` /
 * `actionPrompt` / `pong` 都不 bump sv，此时恒有 `frame.sv <= appliedSv`，
 * 客户端会把它们全部丢掉，后果是乐观 UI 永远收不到回滚、永远卡在「已下注」状态。
 *
 * ★ 红线 #40：`welcome` 帧恒用 `seq = 0`，且**不参与连续性检查**。
 */
import type { Millis, RoomId } from '../ids.js'
import type { PROTOCOL_VERSION } from '../config/constants.js'
import type { ClientMessage, ServerMessage } from './messages.js'

/** 红线 #40：welcome 帧的 seq 恒为 0。 */
export const WELCOME_SEQ = 0

/** 客户端 → 服务端的一帧。一帧恰好一条消息（客户端不做批量合并）。 */
export interface ClientFrame {
  readonly v: typeof PROTOCOL_VERSION
  /**
   * 会话内单调递增，**跨重连不重置**（重连沿用同一个 sessionId 的计数）。
   * 服务端用双水位跟踪：入队成功推进 `lastAcceptedCid`，
   * step 提交成功才推进 `lastProcessedCid`（红线 #34）。
   */
  readonly cid: number
  /** 客户端本地时钟，**仅用于统计**，服务端不信任（可以是任意值） */
  readonly sentAt: Millis
  readonly msg: ClientMessage
}

/**
 * 服务端 → 客户端的一帧。
 *
 * 同一个 `step` 产生的多条消息**必须合并进同一帧**（§5.7）：6 次 ws.send 变 1 次，
 * 客户端一次 reducer + 一次渲染，避免「底池已清空但公共牌还没出来」的中间态闪烁。
 * 唯一例外是跑马（`runItOut`）：各街的 `communityCards` 必须分帧发，否则无法慢放。
 */
export interface ServerFrame {
  readonly v: typeof PROTOCOL_VERSION
  /** 每 session 严格 +1。welcome 恒为 0 且不参与连续性检查（红线 #40） */
  readonly seq: number
  /** 本帧应用后房间的 stateVersion；无房间上下文时为 0 */
  readonly sv: number
  /** 服务端进程 ULID，变化即要求客户端整表重置 */
  readonly epoch: string
  /** 带 sv 的帧必带 */
  readonly room?: RoomId
  /** 服务器时钟。客户端用它做时钟偏移估计 */
  readonly st: Millis
  /** = lastProcessedCid，piggyback 回执（红线 #34 的低水位） */
  readonly ack?: number
  readonly msgs: readonly ServerMessage[]
}

// ============================================================================
// 客户端应用规则（§5.2）——放在 shared 是为了让它可单测、且前后端口径一致
// ============================================================================

/** 客户端持有的、与帧顺序相关的全部状态。 */
export interface FrameCursor {
  /** 上次接受的 epoch；初始为 null */
  readonly epoch: string | null
  readonly appliedSeq: number
  readonly appliedSv: number
}

export const INITIAL_FRAME_CURSOR: FrameCursor = { epoch: null, appliedSeq: 0, appliedSv: 0 }

export type FrameVerdict =
  /** 正常应用；若 reset 为 true，先清空本地全部房间状态 */
  | 'apply'
  /** 重复或过期帧，静默丢弃 */
  | 'duplicate'
  /** 缺口：必须 requestSnapshotOnce('gap')，**不要**应用本帧 */
  | 'gap'

export interface FrameDecision {
  readonly verdict: FrameVerdict
  /** epoch 变化：应用前先 resetAll() */
  readonly reset: boolean
  /** verdict === 'apply' 时的新游标 */
  readonly next: FrameCursor
}

/**
 * §5.2 客户端应用规则的纯函数版本。
 *
 * ★ 唯一的丢帧条件是 `seq <= appliedSeq`。**绝不允许**加上 `sv <= appliedSv`
 *   这一条——那会吞掉所有私有消息（见文件头注释）。
 *
 * ★ 收到 `gap` 后必须走 `requestSnapshotOnce`（单飞 + 2s 节流，红线 #49）：
 *   一旦丢一帧，之后每条帧都满足 `seq !== appliedSeq + 1`，
 *   不节流就会对每条都发一次 requestSnapshot，被 20 msg/s 限流 close(4429)，
 *   退避重连后再来一次——弱网下必然雪崩。
 */
export function classifyFrame(cursor: FrameCursor, frame: ServerFrame): FrameDecision {
  // 服务端换进程：本地全部状态作废（红线 #42 在途手牌已被作废，补不回来）
  if (cursor.epoch !== null && frame.epoch !== cursor.epoch) {
    return {
      verdict: 'apply',
      reset: true,
      next: { epoch: frame.epoch, appliedSeq: frame.seq, appliedSv: frame.sv },
    }
  }
  const base: FrameCursor =
    cursor.epoch === null ? { ...cursor, epoch: frame.epoch } : cursor

  // welcome：跳过连续性检查（红线 #40）。它本身就是重新对齐的起点
  if (frame.seq === WELCOME_SEQ) {
    return {
      verdict: 'apply',
      reset: false,
      next: { epoch: frame.epoch, appliedSeq: base.appliedSeq, appliedSv: Math.max(base.appliedSv, frame.sv) },
    }
  }
  if (frame.seq <= base.appliedSeq) {
    return { verdict: 'duplicate', reset: false, next: base }
  }
  if (frame.seq !== base.appliedSeq + 1) {
    return { verdict: 'gap', reset: false, next: base }
  }
  return {
    verdict: 'apply',
    reset: false,
    next: { epoch: frame.epoch, appliedSeq: frame.seq, appliedSv: Math.max(base.appliedSv, frame.sv) },
  }
}

/**
 * 快照择新：**只有 `roomSnapshot` 按 sv 判定**，其余消息一律直接 apply。
 * 这是 `sv` 在客户端的唯一用途。
 */
export function shouldApplySnapshot(snapshotSv: number, appliedSv: number): boolean {
  return snapshotSv >= appliedSv
}

/**
 * 新会话（`welcome.resumed === false`）必须把游标归零。
 *
 * ★ 这一条容易漏：服务端对**新** session 的 seq 从 1 开始，而客户端手里
 *   还留着上一个 session 的 `appliedSeq`（可能是 57）。不归零的话，
 *   新会话的前 57 帧全部满足 `seq <= appliedSeq`，会被 `classifyFrame`
 *   判成 `duplicate` 静默丢弃，UI 表现为「连上了但一直是空桌」。
 *   `resumed === true` 时**不要**调用它——那正是要沿用旧游标做缺口检测。
 */
export function cursorForNewSession(epoch: string): FrameCursor {
  return { epoch, appliedSeq: 0, appliedSv: 0 }
}
